import { ArrowRightIcon } from 'lucide-react'
import { toast } from 'sonner'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { Button, usePageTitle } from 'slab'
import { usePostQuotingProspectsProspectIdQuotesIdFinalize } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { CenteredLayout } from '../CenteredLayout'
import { useEditingAllowances } from '../EditingAllowancesProvider'
import { useEmployer } from '../EmployerProvider'
import { useQuote } from '../QuoteProvider'
import { emitEmployerOnboardingEvent } from '../utils'
import { CottonCandyContainer } from './CottonCandyContainer'
import { QuoteCostBreakdown } from './QuoteCostBreakdown'
import { useSelfServeExplorerContext } from './QuoteSelfServeExplorerContext'

export const QuoteReviewPage = () => {
  usePageTitle('Review your allowance strategy')

  const { editingAllowances, returnUrl } = useEditingAllowances()
  const { assign } = useWindowLocation()

  const { employer } = useEmployer()
  const { quote, shouldShowPendingPricesWarning } = useQuote()
  const { totalEmployerCost } = useSelfServeExplorerContext()
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()

  const finalizeQuoteMutation = usePostQuotingProspectsProspectIdQuotesIdFinalize()
  const showWarning = shouldShowPendingPricesWarning && quote?.preliminary

  const footerNote = showWarning
    ? 'Actual costs may vary based on the final plan costs set by insurance companies, the number of employees that enroll, and your employees\' age, location, number of dependents, and plan selections.'
    : 'Actual costs may vary based on the number of employees that enroll, and your employees\' age, location, number of dependents, and plan selections.'

  const onAcceptQuote = async () => {
    if (!employer?.quoting_prospect_id || !quote?.id) {
      toast.error('Missing required data to finalize quote')
      return
    }

    emitEmployerOnboardingEvent('[ER] Allowance reviewed', {
      monthly_cost: totalEmployerCost
    })

    try {
      await finalizeQuoteMutation.mutateAsync({
        prospectId: employer.quoting_prospect_id,
        id: quote.id,
      })

      if (editingAllowances) {
        // Go directly to dashboard when editing
        assign(returnUrl)
      } else {
        navigateToNextStep() // Normal flow
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to finalize quote'
      toast.error(errorMessage)
    }
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header heading="Let's review your estimated monthly costs" subheading={showWarning ? 'Once we have final costs available, we\'ll email you with an updated estimate.' : undefined} />
      <div className='mb-10'>
        <CottonCandyContainer>
          <QuoteCostBreakdown showStaticValue />
        </CottonCandyContainer>
      </div>
      <div className='mb-16 body-xs text-gray-500 text-center'>
        {footerNote}
        <br />
        Once you send invites to your employees, you cannot change your allowances.
      </div>
      <CenteredLayout.Buttons>
        <Button variant='secondary' onClick={navigateToPreviousStep}>
          Back
        </Button>
        <Button
          variant='primary'
          onClick={onAcceptQuote}
          disabled={finalizeQuoteMutation.isPending}
          rightIcon={editingAllowances ? undefined : ArrowRightIcon}
        >
          {editingAllowances ? 'Save my Changes' : 'Next step'}
        </Button>
      </CenteredLayout.Buttons>
    </CenteredLayout>
  )
}
